import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, UsersIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DefaultService, Player, GameOutput, PlayOutput, PlayUpdateInput } from '../generated-api';
import SortableEntity, { RankingEntity, Team, TEAM_COLORS } from '../components/SortableEntity';
import SelectPlayersCard from '../components/SelectPlayersCard';
import GameDetailsForm from '../components/GameDetailsForm';
import LeagueSelectionGrid from '../components/LeagueSelectionGrid';
import { useRankingLogic } from '../hooks/useRankingLogic';
import { useTeamManagement } from '../hooks/useTeamManagement';
import { useData } from '../contexts/DataContext';
import { trackAction } from '../services/telemetry';



const PlayEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { games, leagues, fetchGames, fetchLeagues } = useData();

  const [play, setPlay] = useState<PlayOutput | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [rankingEntities, setRankingEntities] = useState<RankingEntity[]>([]);
  
  // Play details
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [selectedGame, setSelectedGame] = useState<GameOutput | null>(null);
  const [gameExtensions, setGameExtensions] = useState<string[]>([]);
  const [playDate, setPlayDate] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [playImages, setPlayImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState<string[]>([]);
  const [durationMinutes, setDurationMinutes] = useState<number | null>(null);
  const [teamMetadata, setTeamMetadata] = useState<Record<string, Record<string, any>>>({});
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshingRatings, setRefreshingRatings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [originalTeamNames, setOriginalTeamNames] = useState<Record<string, string>>({});

  // Use custom hooks
  const {
    teams,
    setTeams,
    rankedPlayers,
    setRankedPlayers,
    addPlayerToTeam,
    removePlayerFromTeam,
    getPlayerTeam,
    getTeamColor
  } = useTeamManagement();

  const {
    draggedOverId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    createTie,
    separateFromTie,
    updateRankingEntities
  } = useRankingLogic();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setError(null);
      // Use cached games and leagues
      const [playData, playersData, gamesData] = await Promise.all([
          DefaultService.getPlayPlaysPlayIdGet({ playId: id }),
          DefaultService.getPlayersPlayersGet(),
          fetchGames(),
          fetchLeagues()
      ]);

      setPlay(playData);
      setPlayers(playersData);

      // Initialize form fields
      setSelectedGameId(playData.gameId);
      setGameExtensions(playData.gameExtensions || []);
      setComment(playData.comment || '');
      setExistingImages(playData.imageUrls || []);
      setSelectedLeagueIds(playData.leagueIds || []);
      setDurationMinutes(playData.durationMinutes || null);
      setTeamMetadata(playData.teamMetadata || {});

      // Convert play date to datetime-local format
      if (playData.playDate) {
        const date = new Date(playData.playDate);
        const localISOTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setPlayDate(localISOTime);
      }

      // Set selected game
      const game = gamesData.find(g => g.id === playData.gameId);
      setSelectedGame(game || null);
      
      // Extract all player IDs from teams
      const allPlayerIds = Array.from(new Set(Object.values(playData.teams).flat()));
      setSelectedPlayerIds(allPlayerIds);
      
      // Store original team names for single-player teams (needed for API submission)
      const teamNameMapping: Record<string, string> = {};
      Object.entries(playData.teams).forEach(([teamName, playerIds]) => {
        if (playerIds.length === 1) {
          teamNameMapping[playerIds[0]] = teamName;
        }
      });
      setOriginalTeamNames(teamNameMapping);
      
      // Convert teams from API format to new component format
      const convertedTeams: Team[] = [];
      Object.entries(playData.teams).forEach(([teamName, playerIds], index) => {
        if (playerIds.length > 1) { // Only convert multi-player teams
          const availableColor = TEAM_COLORS[convertedTeams.length % TEAM_COLORS.length];
          convertedTeams.push({
            id: `team-${Date.now()}-${index}`,
            name: teamName,
            color: availableColor.name.toLowerCase(),
            playerIds: playerIds
          });
        }
      });
      setTeams(convertedTeams);
      
      // Build ranking entities from the ranking data
      const entities: RankingEntity[] = [];
      let position = 1;
      
      playData.ranking.forEach((rankItem) => {
        if (typeof rankItem === 'string') {
          // Single entity (either team name or player name)
          const teamPlayers = playData.teams[rankItem];
          if (teamPlayers && teamPlayers.length > 1) {
            // This is a team
            const team = convertedTeams.find(t => t.name === rankItem);
            if (team) {
              entities.push({
                id: team.id,
                type: 'team',
                position: position,
                tiedWith: []
              });
            }
          } else if (teamPlayers && teamPlayers.length === 1) {
            // This is an individual player (stored as single-player "team")
            entities.push({
              id: teamPlayers[0],
              type: 'player',
              position: position,
              tiedWith: []
            });
          }
          position++;
        } else {
          // Tie (array of team/player names)
          const tieEntityIds: string[] = [];
          
          rankItem.forEach(name => {
            const teamPlayers = playData.teams[name];
            if (teamPlayers && teamPlayers.length > 1) {
              // This is a team in the tie
              const team = convertedTeams.find(t => t.name === name);
              if (team) {
                tieEntityIds.push(team.id);
              }
            } else if (teamPlayers && teamPlayers.length === 1) {
              // This is a player in the tie
              tieEntityIds.push(teamPlayers[0]);
            }
          });
          
          // Add all tied entities with same position and cross-references
          tieEntityIds.forEach(entityId => {
            const isTeam = convertedTeams.some(t => t.id === entityId);
            entities.push({
              id: entityId,
              type: isTeam ? 'team' : 'player',
              position: position,
              tiedWith: tieEntityIds.filter(id => id !== entityId)
            });
          });
          
          position += tieEntityIds.length;
        }
      });
      
      setRankingEntities(entities);
      
    } catch (err) {
      setError('Failed to load play data. Please try again.');
      console.error('Error loading play:', err);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, setTeams]); // fetchGames and fetchLeagues are stable from useData context

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Update selected game when selectedGameId changes
    const game = games.find(g => g.id === selectedGameId);
    setSelectedGame(game || null);
  }, [selectedGameId, games]);

  // Update ranking entities when teams or selected players change (but preserve existing ranking on edit page)
  useEffect(() => {
    if (!isInitialLoad && selectedPlayerIds.length > 0) {
      // On edit page, only update if we don't have existing ranking entities with proper positions
      setRankingEntities(prev => {
        // If we have existing entities with proper positions, preserve them and just add/remove players as needed
        if (prev.length > 0 && prev.some(e => e.position > 0)) {
          // Check if we need to add new entities for players not already ranked
          const existingIds = new Set(prev.map(e => e.id));
          const newEntities = [...prev];
          
          // Add teams that aren't in the ranking yet
          teams.forEach(team => {
            if (!existingIds.has(team.id)) {
              newEntities.push({
                id: team.id,
                type: 'team',
                position: Math.max(...newEntities.map(e => e.position), 0) + 1,
                tiedWith: []
              });
            }
          });
          
          // Add individual players (not in teams) that aren't in the ranking yet
          selectedPlayerIds.forEach(playerId => {
            const isInTeam = teams.some(team => team.playerIds.includes(playerId));
            if (!isInTeam && !existingIds.has(playerId)) {
              newEntities.push({
                id: playerId,
                type: 'player',
                position: Math.max(...newEntities.map(e => e.position), 0) + 1,
                tiedWith: []
              });
            }
          });
          
          // Remove entities that are no longer selected
          const validIds = new Set([...teams.map(t => t.id), ...selectedPlayerIds.filter(pid => !teams.some(team => team.playerIds.includes(pid)))]);
          const filteredEntities = newEntities.filter(e => validIds.has(e.id));
          
          return filteredEntities;
        } else {
          // No existing ranking, create fresh one
          const entities = updateRankingEntities(teams, selectedPlayerIds);
          return entities;
        }
      });
    }
  }, [teams, selectedPlayerIds, updateRankingEntities, isInitialLoad]);

  const togglePlayerSelection = (playerId: string) => {
    setSelectedPlayerIds(prev => 
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const toggleExtension = (extension: string) => {
    setGameExtensions(prev =>
      prev.includes(extension)
        ? prev.filter(ext => ext !== extension)
        : [...prev, extension]
    );
  };

  const toggleLeagueSelection = (leagueId: string) => {
    setSelectedLeagueIds(prev => {
      const isSelected = prev.includes(leagueId);
      const league = leagues.find(l => l.id === leagueId);
      const globalLeague = leagues.find(l => l.isGlobal);
      
      // Special handling for Unranked league - mutually exclusive with all other leagues
      if (league?.name === 'Unranked') {
        if (isSelected) {
          // Deselecting Unranked - select Global league as default
          return globalLeague ? [globalLeague.id] : [];
        } else {
          // Selecting Unranked - deselect all others
          return [leagueId];
        }
      }
      
      // If Unranked is currently selected, deselect it when selecting any other league
      const unrankedLeague = leagues.find(l => l.name === 'Unranked');
      if (unrankedLeague && prev.includes(unrankedLeague.id) && !isSelected) {
        // When selecting any other league, ensure Global is also selected
        const newSelection = [leagueId];
        if (globalLeague && leagueId !== globalLeague.id) {
          newSelection.push(globalLeague.id);
        }
        return newSelection;
      }
      
      if (league?.isGlobal) {
        // Global league cannot be deselected if it's the only one selected
        if (isSelected && prev.length === 1) {
          return prev;
        }
      } else if (!isSelected) {
        // When selecting a non-Global league, also ensure Global is selected
        const newSelection = isSelected
          ? prev.filter(id => id !== leagueId)
          : [...prev, leagueId];
        
        if (globalLeague && !newSelection.includes(globalLeague.id)) {
          newSelection.push(globalLeague.id);
        }
        return newSelection;
      }
      
      return isSelected
        ? prev.filter(id => id !== leagueId)
        : [...prev, leagueId];
    });
  };

  // Wrap the hook functions to work with local state
  const handleDragEndWrapper = (event: any) => {
    handleDragEnd(event, setRankingEntities);
  };

  const createTieWrapper = (entityId1: string, entityId2: string) => {
    createTie(entityId1, entityId2, setRankingEntities);
  };

  const separateFromTieWrapper = (entityId: string) => {
    separateFromTie(entityId, setRankingEntities);
  };

  const handleDeleteExistingImage = async (imageUrl: string) => {
    if (!id) return;
    
    try {
      setError(null);
      await DefaultService.deletePlayImagesPlaysPlayIdImagesDelete({ playId: id, requestBody: [imageUrl] });
      
      // Remove from local state
      setExistingImages(prev => prev.filter(url => url !== imageUrl));
      
      console.log('Successfully deleted image:', imageUrl);
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) return;

    // Validation
    if (!selectedGameId) {
      setError('Please select a game.');
      return;
    }

    if (selectedPlayerIds.length < 2) {
      setError('You need at least 2 players to update the play session.');
      return;
    }

    // Validate team count if game has team constraints
    if (selectedGame) {
      const teamsWithPlayers = teams.filter(team => team.playerIds.length > 0);
      const playersNotInTeams = selectedPlayerIds.filter(playerId => 
        !teams.some(team => team.playerIds.includes(playerId))
      );
      const totalTeamCount = teamsWithPlayers.length + playersNotInTeams.length;

      if (selectedGame.minTeams !== undefined && selectedGame.minTeams !== null) {
        if (totalTeamCount < selectedGame.minTeams) {
          if (selectedGame.minTeams === selectedGame.maxTeams) {
            setError(`This game requires exactly ${selectedGame.minTeams} ${selectedGame.minTeams === 1 ? 'team' : 'teams'}. You have ${totalTeamCount} ${totalTeamCount === 1 ? 'team' : 'teams'}.`);
          } else {
            setError(`This game requires at least ${selectedGame.minTeams} ${selectedGame.minTeams === 1 ? 'team' : 'teams'}. You have ${totalTeamCount} ${totalTeamCount === 1 ? 'team' : 'teams'}.`);
          }
          return;
        }
      }

      if (selectedGame.maxTeams !== undefined && selectedGame.maxTeams !== null) {
        if (totalTeamCount > selectedGame.maxTeams) {
          if (selectedGame.minTeams === selectedGame.maxTeams) {
            setError(`This game requires exactly ${selectedGame.maxTeams} ${selectedGame.maxTeams === 1 ? 'team' : 'teams'}. You have ${totalTeamCount} ${totalTeamCount === 1 ? 'team' : 'teams'}.`);
          } else {
            setError(`This game allows at most ${selectedGame.maxTeams} ${selectedGame.maxTeams === 1 ? 'team' : 'teams'}. You have ${totalTeamCount} ${totalTeamCount === 1 ? 'team' : 'teams'}.`);
          }
          return;
        }
      }
    }

    setSubmitting(true);
    setError(null);

    try {
      // Convert to the format expected by the API
      const playTeams: Record<string, string[]> = {};
      
      // Process teams first
      teams.forEach(team => {
        if (team.playerIds.length > 0) {
          playTeams[team.name] = team.playerIds;
        }
      });
      
      // Then add individual players (not in teams) as their own "teams"
      selectedPlayerIds.forEach(playerId => {
        const isInTeam = teams.some(team => team.playerIds.includes(playerId));
        if (!isInTeam) {
          // Use original team name if available, otherwise use player name
          const originalTeamName = originalTeamNames[playerId];
          if (originalTeamName) {
            playTeams[originalTeamName] = [playerId];
          } else {
            const player = players.find(p => p.id === playerId);
            if (player) {
              playTeams[player.name] = [playerId];
            }
          }
        }
      });

      // Convert ranking to API format using rankingEntities
      const sortedEntities = [...rankingEntities].sort((a, b) => a.position - b.position);
      const ranking: (string | string[])[] = [];
      
      const processedPositions = new Set<number>();
      for (const entity of sortedEntities) {
        if (processedPositions.has(entity.position)) continue;
        
        const entitiesAtPosition = sortedEntities.filter(e => e.position === entity.position);
        const namesAtPosition = entitiesAtPosition.map(e => {
          if (e.type === 'team') {
            const team = teams.find(t => t.id === e.id);
            return team?.name || '';
          } else {
            // Individual player - use the original team name if it exists
            const originalTeamName = originalTeamNames[e.id];
            if (originalTeamName) {
              return originalTeamName;
            }
            // Fallback: find the corresponding team name in playTeams
            const player = players.find(p => p.id === e.id);
            if (player) {
              const teamName = Object.keys(playTeams).find(name => 
                playTeams[name].includes(player.id)
              );
              return teamName || '';
            }
            return '';
          }
        }).filter((name, index, arr) => name !== '' && arr.indexOf(name) === index); // Remove duplicates

        if (namesAtPosition.length === 1) {
          ranking.push(namesAtPosition[0]);
        } else if (namesAtPosition.length > 1) {
          ranking.push(namesAtPosition);
        }
        
        processedPositions.add(entity.position);
      }

      // Convert playDate back to ISO string with timezone for backend submission
      let playDateForSubmission = undefined;
      if (playDate) {
        // The playDate is in local datetime format (YYYY-MM-DDTHH:mm)
        // Convert it back to ISO string with timezone
        const localDate = new Date(playDate);
        playDateForSubmission = localDate.toISOString();
      }

      // Map teamMetadata keys to actual team names used in playTeams
      const mappedTeamMetadata: Record<string, Record<string, any>> = {};
      Object.entries(teamMetadata).forEach(([metadataTeamName, metadata]) => {
        // Find the actual team name in playTeams that matches
        const actualTeamName = Object.keys(playTeams).find(teamName => {
          // Check if this is a team or individual player
          const teamPlayers = playTeams[teamName];
          if (teamPlayers.length > 1) {
            // Multi-player team - check if team name matches
            const team = teams.find(t => t.name === metadataTeamName);
            return team && team.playerIds.every(id => teamPlayers.includes(id));
          } else {
            // Single player - check if player name matches or original team name
            const originalTeamName = originalTeamNames[teamPlayers[0]];
            return originalTeamName === metadataTeamName || 
                   (players.find(p => p.id === teamPlayers[0])?.name === metadataTeamName);
          }
        });
        
        if (actualTeamName) {
          mappedTeamMetadata[actualTeamName] = metadata;
        }
      });

      const playUpdateInput: PlayUpdateInput = {
        gameId: selectedGameId,
        gameExtensions: gameExtensions,
        playDate: playDateForSubmission,
        teams: playTeams,
        ranking: ranking,
        comment: comment.trim() || undefined,
        leagueIds: selectedLeagueIds,
        durationMinutes: durationMinutes || undefined,
        teamMetadata: Object.keys(mappedTeamMetadata).length > 0 ? mappedTeamMetadata : undefined
      };

      await DefaultService.updatePlayPlaysPlayIdPut({ playId: id, requestBody: playUpdateInput });
      trackAction('play_edited');
      
      // Upload images separately if provided
      if (playImages.length > 0) {
        await DefaultService.uploadPlayImagesPlaysPlayIdImagesPost({ playId: id, formData: { images: playImages } });
      }
      
      // Show refreshing ratings state
      setRefreshingRatings(true);
      
      // Refresh player data to get updated ratings before navigating
      try {
        const updatedPlayers = await DefaultService.getPlayersPlayersGet();
        setPlayers(updatedPlayers);
      } catch (refreshError) {
        console.warn('Failed to refresh player data:', refreshError);
      } finally {
        setRefreshingRatings(false);
      }
      
      navigate(`/plays/${id}`);
    } catch (err) {
      setError('Failed to update play session. Please try again.');
      console.error('Error updating play:', err);
    } finally {
      setSubmitting(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!play) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-heading mb-4">Play Session Not Found</h2>
        <p className="text-body mb-6">The play session you're trying to edit doesn't exist.</p>
        <Link
          to="/plays"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Play Sessions
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader className="mb-8">
        <Link to="/plays" className="text-primary-500 hover:text-primary-700 mb-4 inline-block">
          ← Back to Play Sessions
        </Link>
        <div className="mb-4">
          <h1 className="text-fluid-h1 font-bold text-heading">Edit Play Session</h1>
          <p className="mt-2 text-body">
            Modify play session details and ranking
          </p>
        </div>
      </PageHeader>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4" data-testid="play-form-error">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800" data-testid="play-form-error-message">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
                data-testid="play-form-error-dismiss"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {players.length === 0 ? (
        <div className="bg-surface shadow rounded-lg p-12 text-center">
          <UsersIcon className="mx-auto h-12 w-12 text-disabled" />
          <h3 className="mt-2 text-sm font-medium text-heading">No players available</h3>
          <p className="mt-1 text-sm text-muted">You need to create players before editing play sessions.</p>
          <div className="mt-6">
            <Link
              to="/players"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              View Players
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Play Details */}
            <div className="lg:col-span-3 mb-6">
              <GameDetailsForm
                games={games}
                selectedGameId={selectedGameId}
                selectedGame={selectedGame}
                gameExtensions={gameExtensions}
                playDate={playDate}
                comment={comment}
                durationMinutes={durationMinutes}
                playImages={playImages}
                existingImages={existingImages}
                teams={(() => {
                  // Get team names from teams structure and individual players
                  const teamNames: string[] = [];
                  teams.forEach(team => {
                    if (team.playerIds.length > 0) {
                      teamNames.push(team.name);
                    }
                  });
                  selectedPlayerIds.forEach(playerId => {
                    const isInTeam = teams.some(team => team.playerIds.includes(playerId));
                    if (!isInTeam) {
                      // Use original team name if available, otherwise use player name
                      const originalTeamName = originalTeamNames[playerId];
                      if (originalTeamName) {
                        teamNames.push(originalTeamName);
                      } else {
                        const player = players.find(p => p.id === playerId);
                        if (player) {
                          teamNames.push(player.name);
                        }
                      }
                    }
                  });
                  return teamNames;
                })()}
                teamMetadata={teamMetadata}
                onTeamMetadataChange={setTeamMetadata}
                onGameSelect={setSelectedGameId}
                onToggleExtension={toggleExtension}
                onPlayDateChange={setPlayDate}
                onCommentChange={setComment}
                onDurationChange={setDurationMinutes}
                onPlayImagesChange={setPlayImages}
                onDeleteExistingImage={handleDeleteExistingImage}
                showAddGameLink={false}
              />
            </div>

            {/* League Selection */}
            <div className="lg:col-span-3 mb-6">
              <LeagueSelectionGrid
                leagues={leagues}
                selectedLeagueIds={selectedLeagueIds}
                onToggleLeagueSelection={toggleLeagueSelection}
              />
            </div>

            {/* Player Selection */}
            <div className="lg:col-span-2">
              <SelectPlayersCard
                players={players}
                selectedPlayerIds={selectedPlayerIds}
                rankedPlayers={rankedPlayers}
                teams={teams}
                onTogglePlayerSelection={togglePlayerSelection}
                onRemovePlayerFromTeam={removePlayerFromTeam}
                getPlayerTeam={getPlayerTeam}
                getTeamColor={getTeamColor}
                addPlayerToTeam={addPlayerToTeam}
                setTeams={setTeams}
                setRankedPlayers={setRankedPlayers}
                addPlayerPath="/players/new"
              />
            </div>

            {/* Ranking */}
            <div>
              <div className="bg-surface shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-heading mb-6">Final Ranking</h2>
                
                {selectedPlayerIds.length === 0 ? (
                  <p className="text-muted text-center py-8">
                    Players will appear here after loading
                  </p>
                ) : (
                  <div>
                    <p className="text-sm text-body mb-4">
                      Drag the left side of each entry to reorder. Click "Tie with" buttons on the right to create ties.
                    </p>
                    
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEndWrapper}
                    >
                      <SortableContext 
                        items={rankingEntities.map(e => e.id)} 
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {rankingEntities
                            .sort((a, b) => a.position - b.position)
                            .map((entity) => (
                              <SortableEntity
                                key={entity.id}
                                entity={entity}
                                teams={teams}
                                allPlayers={players}
                                allEntities={rankingEntities}
                                onSeparateFromTie={separateFromTieWrapper}
                                onCreateTie={createTieWrapper}
                                isDraggedOver={draggedOverId === entity.id}
                              />
                            ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-edge">
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={submitting || selectedPlayerIds.length < 2 || refreshingRatings}
                      className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {refreshingRatings ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Updating Ratings...
                        </div>
                      ) : submitting ? 'Updating Play Session...' : 'Update Play Session'}
                    </button>
                    <Link
                      to="/plays"
                      className="flex-1 flex justify-center items-center py-2 px-4 border border-edge-input rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Cancel
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default PlayEditPage;